import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import DocSidePanelTabs from '../components/DocSidePanelTabs';
import ExcelViewer from '../components/ExcelViewer';
import IfcViewer from '../components/IfcViewer';
import PdfViewer, { PdfAnnotation } from '../components/PdfViewer';
import ValidateDocumentModal from '../components/ValidateDocumentModal';

type Version = {
  id: number;
  version_number: number;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_at: string;
  uploaded_by_name: string | null;
  file_key: string;
};

type Doc = {
  id: number;
  project_id: number;
  title: string;
  status: 'a_consulter' | 'a_traiter' | 'valide';
  urgency: 'normal' | 'urgent' | 'critique';
  deadline: string | null;
  notes: string | null;
  project_name: string;
  created_by: number;
  assigned_to: number | null;
};

export default function DocumentView() {
  const { docId } = useParams<{ docId: string }>();
  const nav = useNavigate();
  const [doc, setDoc] = useState<Doc | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [annotations, setAnnotations] = useState<PdfAnnotation[]>([]);
  const [selectedVer, setSelectedVer] = useState<Version | null>(null);
  const [uploading, setUploading] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [validateOpen, setValidateOpen] = useState(false);

  async function refresh() {
    if (!docId) return;
    const r = await api.get<{ document: Doc; versions: Version[]; annotations: PdfAnnotation[] }>(
      `/documents/${docId}`
    );
    setDoc(r.document);
    setVersions(r.versions);
    setAnnotations(r.annotations);
    setSelectedVer((prev) => {
      if (prev) {
        const updated = r.versions.find((v) => v.id === prev.id);
        if (updated) return updated;
      }
      return r.versions[0] ?? null;
    });
  }

  useEffect(() => {
    refresh();
  }, [docId]);

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f || !docId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`/api/v1/documents/${docId}/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload impossible' }));
        alert((err as { error?: string }).error ?? 'Upload impossible');
      } else {
        refresh();
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function openValidate() {
    setValidateOpen(true);
  }

  async function onReview() {
    if (!docId) return;
    await api.post(`/documents/${docId}/revoir`, { note: reviewNote || null });
    setReviewNote('');
    refresh();
  }

  async function onAddText(a: { page: number; x: number; y: number; content: string }) {
    if (!docId || !selectedVer) return;
    await api.post(`/documents/${docId}/annotations`, {
      type: 'texte',
      content: a.content,
      page: a.page,
      x: a.x,
      y: a.y,
      version_id: selectedVer.id,
    });
    refresh();
  }

  async function onAddHighlight(a: {
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
    content?: string;
  }) {
    if (!docId || !selectedVer) return;
    await api.post(`/documents/${docId}/annotations`, {
      type: 'surlignage',
      content: a.content ?? null,
      page: a.page,
      x: a.x,
      y: a.y,
      width: a.width,
      height: a.height,
      version_id: selectedVer.id,
    });
    refresh();
  }

  async function onAddVoice(a: { page: number; x: number; y: number; audio: Blob }) {
    if (!docId || !selectedVer) return;
    const fd = new FormData();
    fd.append('audio', a.audio, 'memo.webm');
    fd.append('page', String(a.page));
    fd.append('x', String(a.x));
    fd.append('y', String(a.y));
    fd.append('version_id', String(selectedVer.id));
    const res = await fetch(`/api/v1/documents/${docId}/voice-memo`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      alert("Impossible d'enregistrer le mémo vocal.");
      return;
    }
    refresh();
  }

  async function onDeleteAnnotation(id: number) {
    await api.del(`/annotations/${id}`);
    refresh();
  }

  async function onAddFreeComment(content: string) {
    if (!docId || !content.trim() || !selectedVer) return;
    await api.post(`/documents/${docId}/annotations`, {
      type: 'texte',
      content: content.trim(),
      version_id: selectedVer.id,
    });
    refresh();
  }

  if (!doc) return <div className="card card-meta">Chargement…</div>;

  const fileUrl = selectedVer
    ? `/api/v1/files?key=${encodeURIComponent(selectedVer.file_key)}`
    : null;
  const isPdf = selectedVer?.mime_type === 'application/pdf';
  const isImage = selectedVer?.mime_type?.startsWith('image/') ?? false;
  const isIfc =
    (selectedVer?.filename ?? '').toLowerCase().endsWith('.ifc') ||
    selectedVer?.mime_type === 'application/x-step' ||
    selectedVer?.mime_type === 'application/ifc';
  const nameLow = (selectedVer?.filename ?? '').toLowerCase();
  const isExcel =
    (selectedVer?.mime_type ?? '').includes('spreadsheet') ||
    (selectedVer?.mime_type ?? '').includes('excel') ||
    nameLow.endsWith('.xlsx') ||
    nameLow.endsWith('.xls') ||
    nameLow.endsWith('.xlsm') ||
    nameLow.endsWith('.xlsb');

  // En haut-gauche de la toolbar
  const toolbarLeading = (
    <button
      className="pdf-mode-btn"
      onClick={() => nav(`/projets/${doc.project_id}/documents`)}
      title="Retour à la liste des documents du projet"
    >
      ← Retour au projet
    </button>
  );

  // À droite de la toolbar
  const toolbarExtras = (
    <>
      <label className="pdf-mode-btn" style={{ cursor: 'pointer' }} title="Déposer une nouvelle version">
        {uploading ? 'Upload…' : '+ Version'}
        <input ref={fileRef} type="file" onChange={onUpload} style={{ display: 'none' }} />
      </label>
      {selectedVer && fileUrl && (
        <a
          className="pdf-mode-btn"
          href={`${fileUrl}&download=1`}
          target="_blank"
          rel="noreferrer"
          title="Télécharger"
        >
          ↓
        </a>
      )}
      <button
        className="pdf-mode-btn"
        style={{ background: 'var(--c-btn-valider)', color: '#fff', borderColor: 'transparent' }}
        onClick={openValidate}
        disabled={doc.status === 'valide'}
      >
        ✓ Valider
      </button>
      <button
        className="pdf-mode-btn"
        style={{ background: 'var(--c-btn-revoir)', color: '#fff', borderColor: 'transparent' }}
        onClick={onReview}
      >
        ↻ À revoir
      </button>
      <button
        className="pdf-mode-btn"
        onClick={() => setPanelOpen((v) => !v)}
        title={panelOpen ? 'Masquer le panneau latéral' : 'Afficher le panneau latéral'}
      >
        {panelOpen ? '⇨' : '⇦'}
      </button>
    </>
  );

  // Pour l'overlay fullscreen "volet du document" : volet complet (4 onglets dont Assistant)
  const renderSidePanel = () => (
    <DocSidePanelTabs
      doc={doc}
      versions={versions}
      annotations={annotations}
      selectedVer={selectedVer}
      setSelectedVer={setSelectedVer}
      reviewNote={reviewNote}
      setReviewNote={setReviewNote}
      onAddFreeComment={onAddFreeComment}
      onDeleteAnnotation={onDeleteAnnotation}
    />
  );

  return (
    <div className={`doc-view ${panelOpen ? 'with-panel' : 'no-panel'}`}>
      <div className="doc-body">
        <div className="doc-main">
          {!selectedVer && (
            <div className="doc-empty-wrap">
              <div className="doc-empty-topbar">
                <button
                  className="pdf-mode-btn"
                  onClick={() => nav(`/projets/${doc.project_id}/documents`)}
                >
                  ← Retour au projet
                </button>
              </div>
              <div className="doc-empty-hero">
                <div className="doc-empty-icon">📄</div>
                <h2 className="doc-empty-title">{doc.title}</h2>
                <p className="doc-empty-sub">
                  Ce document n'a pas encore de fichier. Dépose la première version pour lancer le
                  cycle de validation.
                </p>
                <label className="btn btn-primary doc-empty-btn">
                  {uploading ? 'Upload en cours…' : '+ Déposer un fichier'}
                  <input
                    ref={fileRef}
                    type="file"
                    onChange={onUpload}
                    style={{ display: 'none' }}
                  />
                </label>
                <div className="card-meta" style={{ marginTop: 14, fontSize: 12 }}>
                  Formats supportés : PDF, images, IFC, Word, autres fichiers (100 Mo max)
                </div>
              </div>
            </div>
          )}
          {selectedVer && isPdf && fileUrl && (
            <PdfViewer
              fileUrl={fileUrl}
              annotations={annotations}
              onAddText={onAddText}
              onAddHighlight={onAddHighlight}
              onAddVoice={onAddVoice}
              onDelete={onDeleteAnnotation}
              toolbarLeading={toolbarLeading}
              toolbarExtras={toolbarExtras}
              renderSidePanel={renderSidePanel}
            />
          )}
          {selectedVer && !isPdf && (
            <div className="non-pdf-wrap">
              <div className="non-pdf-toolbar">
                <div style={{ marginRight: 'auto' }}>{toolbarLeading}</div>
                {toolbarExtras}
              </div>
              {isImage && fileUrl && (
                <img
                  src={fileUrl}
                  alt={selectedVer.filename}
                  style={{ maxWidth: '100%', borderRadius: 10 }}
                />
              )}
              {isIfc && fileUrl && (
                <IfcViewer
                  url={fileUrl}
                  height="calc(100vh - 140px)"
                  documentId={doc.id}
                  versionId={selectedVer.id}
                  annotations={annotations as unknown as import('../components/IfcViewer').IfcAnnotation[]}
                  onAnnotationsChanged={refresh}
                />
              )}
              {isExcel && fileUrl && <ExcelViewer fileUrl={fileUrl} />}
              {!isImage && !isIfc && !isExcel && fileUrl && (
                <div className="card">
                  <div className="card-title">Prévisualisation non supportée</div>
                  <div className="card-meta" style={{ marginBottom: 10 }}>
                    Type : {selectedVer.mime_type ?? 'inconnu'}
                  </div>
                  <a
                    className="btn btn-voir"
                    href={`${fileUrl}&download=1`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Télécharger
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {panelOpen && (
          <aside className="doc-side">
            <DocSidePanelTabs
              doc={doc}
              versions={versions}
              annotations={annotations}
              selectedVer={selectedVer}
              setSelectedVer={setSelectedVer}
              reviewNote={reviewNote}
              setReviewNote={setReviewNote}
              onAddFreeComment={onAddFreeComment}
              onDeleteAnnotation={onDeleteAnnotation}
            />
          </aside>
        )}
      </div>
      <ValidateDocumentModal
        projectId={doc.project_id}
        documentId={doc.id}
        open={validateOpen}
        onClose={() => setValidateOpen(false)}
        onValidated={refresh}
      />
    </div>
  );
}
